/**
 * v2 ChassisDetail — the centrepiece.
 *
 * Replaces v1's 14-card always-open page with a tabs + collapsibles
 * layout (UI v2.0 goals a + f). Once an operator picks a chassis,
 * every common troubleshooting / change / test workflow completes
 * here without leaving or refreshing the page.
 *
 * Surface map (v0.34.0 IA — full-GPON chassis):
 *   <Header>                        — name, adapter chip, locks banner
 *   <Tabs>
 *     Inventory  (default)          — port grid + Run-a-job + InlineJobTracker
 *     Provisioning                  — sub-tabs: Pending / Moves /
 *                                     Manual ops / Config profiles
 *     Diagnostics                   — sub-tabs: Alarms / Backups (+ verify)
 *     Activity                      — recent jobs + transcript drawer
 *     Administration                — info / overrides / schedules / auto
 *   </Tabs>
 *
 * Huawei VDSL keeps its flat reduced set (Inventory / Backups /
 * Alarms / Activity / Administration); read-only adapters
 * (Ubiquiti, Calix) keep Inventory / Activity / Administration.
 *
 * URL hash drives the active tab + sub-tab
 * (``#tab=provisioning&sub=moves``) so deep-links land on the right
 * surface and back/forward navigation preserves context. Legacy
 * flat-tab hashes (``#tab=pending``, ``#tab=deployment``, …) are
 * canonicalised into the new scheme on full-GPON chassis.
 */
import { useQuery } from "@tanstack/react-query";
import { ChevronLeft, Terminal as TerminalIcon } from "lucide-react";
import {
  useCallback,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from "react";
import { Link, useParams } from "react-router-dom";

import { api } from "../../../api/client";
import { Badge } from "../../components/Badge";
import { Tabs, type TabSpec } from "../../components/Tabs";
import { HelpButton } from "../../help/HelpPanel";
import { ActivityTab } from "./tabs/ActivityTab";
import { AdministrationTab } from "./tabs/AdministrationTab";
import { AlarmsTab } from "./tabs/AlarmsTab";
import { BackupsTab } from "./tabs/BackupsTab";
import {
  ManualOpsPanel,
  ProfilesPanel,
  VerifyPanel,
} from "./tabs/DeploymentTab";
import { InventoryTab } from "./tabs/InventoryTab";
import { PendingMovesTab } from "./tabs/PendingMovesTab";
import { PendingTab } from "./tabs/PendingTab";

type ChassisOut = {
  id: number;
  gigafy_device_id: string | null;
  gigafy_device_name: string | null;
  device_name_override: string | null;
  effective_device_name: string;
  gigafy_location_id: string | null;
  gigafy_location_name: string | null;
  adapter_key: string;
  host_synced: string | null;
  host_override: string | null;
  ssh_port_override: number | null;
  ssh_username_override: string | null;
  password_set: boolean;
  concurrent_session_limit: number;
  enabled: boolean;
  last_seen_at: string | null;
  // Effective values come back denormalised on the row; v1's logic
  // is in apps/web/app/routers/chassis.py.
  effective_host: string | null;
  effective_port: number;
  effective_username: string | null;
};

type Lock = {
  id: number;
  scope_key: string;
  holder_gigafy_username: string;
  job_id: string | null;
  hard: boolean;
  acquired_at: string;
};

type LockListResponse = { locks: Lock[] };

/** Read the active tab from the URL hash (``#tab=provisioning``). */
function readHashTab(fallback: string): string {
  if (typeof window === "undefined") return fallback;
  const hash = window.location.hash || "";
  const match = hash.match(/[#&]tab=([\w-]+)/);
  return match ? match[1] : fallback;
}

/** Read the active sub-tab (``&sub=moves``) — same shape as the
 *  AdministrationTab / ManualOpsPanel hash helpers. */
function readHashSub(fallback: string): string {
  if (typeof window === "undefined") return fallback;
  const hash = window.location.hash || "";
  const match = hash.match(/[#&]sub=([\w-]+)/);
  return match ? match[1] : fallback;
}

/** Write the active tab to the URL hash without triggering a navigation. */
function writeHashTab(value: string) {
  if (typeof window === "undefined") return;
  const newHash = `#tab=${value}`;
  if (window.location.hash !== newHash) {
    history.replaceState(null, "", `${window.location.pathname}${window.location.search}${newHash}`);
  }
}

/** Write tab + sub together (legacy-hash canonicalisation). */
function writeHashTabSub(tab: string, sub: string) {
  if (typeof window === "undefined") return;
  const newHash = `#tab=${tab}&sub=${sub}`;
  if (window.location.hash !== newHash) {
    history.replaceState(null, "", `${window.location.pathname}${window.location.search}${newHash}`);
  }
}

/** Update ONLY the sub= param, preserving tab + any prefill params
 *  (op= / port= / …) riding the hash. */
function writeHashSub(value: string) {
  if (typeof window === "undefined") return;
  const url = new URL(window.location.href);
  const hash = url.hash || "#";
  const cleaned = hash.replace(/[&#]sub=[\w-]+/, "");
  url.hash = (cleaned || "#") + (cleaned.includes("=") ? "&" : "") + `sub=${value}`;
  history.replaceState(null, "", url.toString());
}

/**
 * Legacy hash values from the pre-v0.34 flat tab set → their new
 * Provisioning / Diagnostics homes. Only applied on full-GPON chassis
 * — VDSL and read-only adapters keep the flat reduced sets these
 * hashes already name.
 */
function canonicalizeLegacyHash(
  tab: string,
  sub: string,
): { tab: string; sub: string } | null {
  switch (tab) {
    case "pending":
      return { tab: "provisioning", sub: "pending" };
    case "moves":
      return { tab: "provisioning", sub: "moves" };
    case "deployment":
      // Old Deployment sub-tabs: maintenance → Manual ops, profiles →
      // Provisioning → Config profiles, verify → Diagnostics → Backups.
      if (sub === "profiles") return { tab: "provisioning", sub: "profiles" };
      if (sub === "verify") return { tab: "diagnostics", sub: "backups" };
      return { tab: "provisioning", sub: "manual" };
    case "backups":
      return { tab: "diagnostics", sub: "backups" };
    case "alarms":
      return { tab: "diagnostics", sub: "alarms" };
    default:
      return null;
  }
}

export function ChassisDetailPage() {
  const { chassisId } = useParams<{ chassisId: string }>();

  const chassisQ = useQuery<ChassisOut>({
    queryKey: ["v2", "chassis", chassisId],
    queryFn: async () => {
      const res = await api.get<ChassisOut>(`/chassis/${chassisId}`);
      return res.data;
    },
    enabled: !!chassisId,
  });

  const locksQ = useQuery<LockListResponse>({
    queryKey: ["v2", "chassis", chassisId, "locks"],
    queryFn: async () => {
      const res = await api.get<LockListResponse>(
        `/locks?chassis_id=${chassisId}`,
      );
      return res.data;
    },
    enabled: !!chassisId,
    refetchInterval: 5_000,
  });

  // Drives the "Alarms (N)" tab badge. The per-chassis variant of
  // the count endpoint isn't built — we filter the cross-chassis
  // count by issuing a per-chassis list and reading ``total``.
  // Lightweight: 30 s poll, indexed scan.
  const alarmsCountQ = useQuery<{ total: number }>({
    queryKey: ["v2", "chassis", chassisId, "alarms-count"],
    queryFn: async () => {
      const res = await api.get<{ alarms: unknown[]; total: number }>(
        `/chassis/${chassisId}/alarms`,
      );
      return { total: res.data.total };
    },
    enabled: !!chassisId,
    refetchInterval: 30_000,
  });

  // Drives the "Pending (N)" tab badge — count of discovered-but-
  // unprovisioned ONUs from the latest list_pending_onus run.
  const pendingCountQ = useQuery<{ total: number }>({
    queryKey: ["v2", "chassis", chassisId, "pending-count"],
    queryFn: async () => {
      const res = await api.get<{ onus: unknown[] }>(
        `/chassis/${chassisId}/onus/pending`,
      );
      return { total: res.data.onus?.length ?? 0 };
    },
    enabled: !!chassisId,
    refetchInterval: 60_000,
  });

  // Drives the "Moves (N)" tab badge — uncleared port-mismatch alarms
  // correlated to inventory (the server drops stale / in-flight rows).
  const pendingMovesCountQ = useQuery<{ total: number }>({
    queryKey: ["v2", "chassis", chassisId, "pending-moves-count"],
    queryFn: async () => {
      const res = await api.get<{ moves: unknown[] }>(
        `/chassis/${chassisId}/pending-moves`,
      );
      return { total: res.data.moves?.length ?? 0 };
    },
    enabled: !!chassisId,
    refetchInterval: 60_000,
  });

  const [activeTab, setActiveTab] = useState<string>(() =>
    readHashTab("inventory"),
  );

  const onTabChange = useCallback((next: string) => {
    setActiveTab(next);
    writeHashTab(next);
  }, []);

  const adapterKey = chassisQ.data?.adapter_key ?? "";
  // Read-only adapters declare only list_onus + mac_table + optical
  // capabilities. Tabs that fire other job kinds (Diagnostics →
  // backup_running_config / parse_alarms, Provisioning →
  // maintenance ops) would all 400 at the backend capability gate,
  // so hide them at this layer. The Activity tab stays — it's
  // read-only history, transport-agnostic.
  // Members: Ubiquiti UFiber (HTTP) and Calix E7 (SSH).
  const isReadOnly =
    adapterKey === "ubiquiti:gpon" || adapterKey === "calix:gpon";
  // Full-GPON = the only adapters that get the Provisioning /
  // Diagnostics grouped tab set. VDSL + read-only keep their flat
  // reduced sets, so legacy hashes stay valid there.
  const isFullGpon =
    !!chassisQ.data && adapterKey !== "huawei:vdsl" && !isReadOnly;

  // Listen for hash changes (back/forward, manual edit, deep-link)
  // and canonicalise legacy flat-tab hashes on full-GPON chassis.
  useEffect(() => {
    const resolve = () => {
      const tab = readHashTab("inventory");
      if (isFullGpon) {
        const redirect = canonicalizeLegacyHash(tab, readHashSub(""));
        if (redirect) {
          writeHashTabSub(redirect.tab, redirect.sub);
          setActiveTab(redirect.tab);
          return;
        }
      }
      setActiveTab(tab);
    };
    resolve();
    window.addEventListener("hashchange", resolve);
    return () => window.removeEventListener("hashchange", resolve);
  }, [isFullGpon]);

  const tabs = useMemo<TabSpec[]>(() => {
    const cid = chassisId ?? "";
    const chassis = chassisQ.data;
    const alarmCount = alarmsCountQ.data?.total ?? 0;
    const pendingCount = pendingCountQ.data?.total ?? 0;
    const movesCount = pendingMovesCountQ.data?.total ?? 0;
    const alarmsBadge =
      alarmCount > 0 ? (
        <Badge tone={alarmCount > 4 ? "danger" : "warning"}>
          {alarmCount > 99 ? "99+" : alarmCount}
        </Badge>
      ) : undefined;
    const countBadge = (n: number) =>
      n > 0 ? <Badge tone="warning">{n > 99 ? "99+" : n}</Badge> : undefined;
    const inventoryTab: TabSpec = {
      value: "inventory",
      label: "Inventory",
      content: <InventoryTab chassisId={cid} adapterKey={adapterKey} />,
    };
    const activityTab: TabSpec = {
      value: "activity",
      label: "Activity",
      content: <ActivityTab chassisId={cid} />,
    };
    // P3 (added 2026-05-22): low-frequency surfaces promoted from
    // bottom-of-page collapsibles into their own dedicated tab
    // with internal horizontal sub-tabs. Operators only land here
    // when they specifically need overrides / schedules / deploy.
    const adminTabs: TabSpec[] = chassis
      ? [
          {
            value: "admin",
            label: "Administration",
            content: <AdministrationTab chassisId={cid} chassis={chassis} />,
          },
        ]
      : [];
    if (isFullGpon) {
      // v0.34.0 IA — Provisioning + Diagnostics group the old flat
      // Pending / Moves / Deployment / Alarms / Backups tabs. Badges
      // follow their content onto the sub-tabs; the parent tabs carry
      // an aggregate so at-a-glance visibility survives the grouping.
      return [
        inventoryTab,
        {
          value: "provisioning",
          label: "Provisioning",
          badge: countBadge(pendingCount + movesCount),
          content: (
            <ProvisioningTab
              chassisId={cid}
              adapterKey={adapterKey}
              gigafyLocationId={chassis?.gigafy_location_id ?? null}
              pendingBadge={countBadge(pendingCount)}
              movesBadge={countBadge(movesCount)}
            />
          ),
        },
        {
          value: "diagnostics",
          label: "Diagnostics",
          badge: alarmsBadge,
          content: <DiagnosticsTab chassisId={cid} alarmsBadge={alarmsBadge} />,
        },
        activityTab,
        ...adminTabs,
      ];
    }
    // Reduced sets — Huawei VDSL keeps its flat Backups + Alarms tabs
    // (no provisioning/maintenance surface yet); read-only adapters
    // (Ubiquiti, Calix) have nothing to provision or back up.
    return [
      inventoryTab,
      ...(isReadOnly
        ? []
        : [
            {
              value: "backups",
              label: "Backups",
              content: <BackupsTab chassisId={cid} />,
            },
            {
              value: "alarms",
              label: "Alarms",
              badge: alarmsBadge,
              content: <AlarmsTab chassisId={cid} />,
            },
          ]),
      activityTab,
      ...adminTabs,
    ];
  }, [
    chassisId,
    chassisQ.data,
    adapterKey,
    isReadOnly,
    isFullGpon,
    alarmsCountQ.data,
    pendingCountQ.data,
    pendingMovesCountQ.data,
  ]);

  if (chassisQ.isLoading) {
    return <div className="text-sm text-slate-500">Loading chassis…</div>;
  }
  if (!chassisQ.data) {
    return (
      <div className="rounded border border-red-800 bg-red-900/40 p-4 text-sm text-red-200">
        Chassis not found.
      </div>
    );
  }
  const chassis = chassisQ.data;
  const locks = locksQ.data?.locks ?? [];

  return (
    <div className="flex flex-col gap-4">
      {/* ----- Header ----- */}
      <header className="flex flex-col gap-2 animate-enter">
        <Link
          to="/"
          className="inline-flex items-center gap-1 text-xs text-slate-400 transition-[scale,color] duration-150 ease-out hover:text-slate-200 active:scale-[0.96]"
        >
          <ChevronLeft className="h-3 w-3" />
          Back to chassis list
        </Link>
        <div className="flex flex-wrap items-baseline gap-3">
          <h1 className="text-xl font-semibold text-slate-100">
            {chassis.effective_device_name}
          </h1>
          <HelpButton topic={`chassis-${activeTab}`} />
          <Badge tone="info">{chassis.adapter_key}</Badge>
          {!chassis.enabled && <Badge tone="muted">disabled</Badge>}
          <span className="text-xs text-slate-500">
            id {chassis.id} · {chassis.effective_host ?? "no host"}:
            {chassis.effective_port}
          </span>
          <Link
            to={`/chassis/${chassis.id}/terminal`}
            className="ml-auto inline-flex items-center gap-1 rounded border border-slate-700 bg-slate-900 px-2 py-1 text-xs text-slate-200 transition-[scale,background-color,color] duration-150 ease-out hover:bg-slate-800 active:scale-[0.96]"
            title="Open SSH terminal session"
          >
            <TerminalIcon className="h-3.5 w-3.5" />
            Terminal
          </Link>
        </div>
        <div className="text-xs text-slate-400">
          Location:{" "}
          {chassis.gigafy_location_id ? (
            <Link
              to={`/locations/${chassis.gigafy_location_id}`}
              className="text-emerald-400 hover:text-emerald-300"
            >
              {chassis.gigafy_location_name ?? chassis.gigafy_location_id}
            </Link>
          ) : (
            <span className="text-slate-500">—</span>
          )}
        </div>
      </header>

      {/* ----- Active locks banner ----- */}
      {locks.length > 0 && (
        <div className="rounded border border-amber-700 bg-amber-900/30 px-3 py-2 text-xs text-amber-200">
          <span className="font-medium">Active lock:</span>{" "}
          {locks
            .map((l) => `${l.holder_gigafy_username} · ${l.scope_key}`)
            .join(" · ")}
        </div>
      )}

      {/* ----- Tabs ----- */}
      {/* v0.34.0 — Provisioning + Diagnostics group the working
          surfaces; sub-tab state rides the hash as &sub=… */}
      <Tabs tabs={tabs} value={activeTab} onValueChange={onTabChange} />
    </div>
  );
}

// ---------------------------------------------------------------------
// Provisioning — Pending / Moves / Manual ops / Config profiles.
// Sub-tab rides the hash (``#tab=provisioning&sub=moves``) exactly
// like the AdministrationTab, so deep-links + back/forward work.
// ---------------------------------------------------------------------

function ProvisioningTab({
  chassisId,
  adapterKey,
  gigafyLocationId,
  pendingBadge,
  movesBadge,
}: {
  chassisId: string;
  adapterKey: string;
  gigafyLocationId: string | null;
  pendingBadge?: ReactNode;
  movesBadge?: ReactNode;
}) {
  const [sub, setSub] = useState<string>(() => readHashSub("pending"));

  const onSubChange = useCallback((next: string) => {
    setSub(next);
    writeHashSub(next);
  }, []);

  useEffect(() => {
    const onHash = () => setSub(readHashSub("pending"));
    window.addEventListener("hashchange", onHash);
    return () => window.removeEventListener("hashchange", onHash);
  }, []);

  const subTabs = useMemo<TabSpec[]>(
    () => [
      {
        value: "pending",
        label: "Pending",
        badge: pendingBadge,
        content: (
          <PendingTab
            chassisId={chassisId}
            adapterKey={adapterKey}
            gigafyLocationId={gigafyLocationId}
          />
        ),
      },
      {
        value: "moves",
        label: "Moves",
        badge: movesBadge,
        content: <PendingMovesTab chassisId={chassisId} />,
      },
      {
        value: "manual",
        label: "Manual ops",
        content: <ManualOpsPanel chassisId={chassisId} />,
      },
      {
        value: "profiles",
        label: "Config profiles",
        content: <ProfilesPanel chassisId={chassisId} />,
      },
    ],
    [chassisId, adapterKey, gigafyLocationId, pendingBadge, movesBadge],
  );
  return <Tabs tabs={subTabs} value={sub} onValueChange={onSubChange} />;
}

// ---------------------------------------------------------------------
// Diagnostics — Alarms / Backups (backup list + verify-config report,
// both derived from the same backup snapshots).
// ---------------------------------------------------------------------

function DiagnosticsTab({
  chassisId,
  alarmsBadge,
}: {
  chassisId: string;
  alarmsBadge?: ReactNode;
}) {
  const [sub, setSub] = useState<string>(() => readHashSub("alarms"));

  const onSubChange = useCallback((next: string) => {
    setSub(next);
    writeHashSub(next);
  }, []);

  useEffect(() => {
    const onHash = () => setSub(readHashSub("alarms"));
    window.addEventListener("hashchange", onHash);
    return () => window.removeEventListener("hashchange", onHash);
  }, []);

  const subTabs = useMemo<TabSpec[]>(
    () => [
      {
        value: "alarms",
        label: "Alarms",
        badge: alarmsBadge,
        content: <AlarmsTab chassisId={chassisId} />,
      },
      {
        value: "backups",
        label: "Backups",
        content: (
          <div className="flex flex-col gap-3">
            <BackupsTab chassisId={chassisId} />
            <VerifyPanel chassisId={chassisId} />
          </div>
        ),
      },
    ],
    [chassisId, alarmsBadge],
  );
  return <Tabs tabs={subTabs} value={sub} onValueChange={onSubChange} />;
}
